/**
 * Кодировщик QR — свой, без зависимостей.
 *
 * Почему не библиотека и не картинка с чужого хоста. CSP панели запрещает
 * внешние источники (docs/09), поэтому «сгенерировать QR на api.qrserver.com»
 * отпадает сразу — и это правильно: адрес запроса содержал бы ПОЛНЫЙ секрет
 * второго фактора, то есть мы отправили бы его третьей стороне. Тянуть в
 * зависимости целый пакет ради одной картинки на одном экране — тоже дорого:
 * это ещё один аудит supply chain на каждый релиз. Здесь ровно то, что нужно:
 * байтовый режим, уровень коррекции M, версии 1..10 (до 152 байт — otpauth-URL
 * панели укладывается с запасом).
 *
 * Секрет наружу не уходит: строка приходит аргументом, рисуется в DOM и нигде
 * не логируется.
 *
 * Реализация — ISO/IEC 18004: поток бит → блоки Рида — Соломона → размещение
 * змейкой → маска с наименьшим штрафом → информация о формате и версии.
 */

export interface QrMatrix {
  /** Сторона в модулях (без «тихой зоны» — её добавляет компонент). */
  size: number
  /** `true` — тёмный модуль. Индексация `[row][col]`. */
  modules: boolean[][]
}

export class QrTooLongError extends Error {
  constructor(length: number) {
    super(`строка длиной ${length} байт не помещается в QR версии 10`)
    this.name = 'QrTooLongError'
  }
}

// ─────────────────────────── таблицы стандарта ───────────────────────────

/** Всего кодовых слов (данные + коррекция) по версиям 1..10. */
const TOTAL_CODEWORDS = [26, 44, 70, 100, 134, 172, 196, 242, 292, 346]

/**
 * Уровень коррекции M: [слов коррекции на блок, блоков группы 1, блоков группы 2].
 * Блоки второй группы на одно кодовое слово данных длиннее — так стандарт
 * распределяет остаток.
 */
const EC_BLOCKS_M: [number, number, number][] = [
  [10, 1, 0],
  [16, 1, 0],
  [26, 1, 0],
  [18, 2, 0],
  [24, 2, 0],
  [16, 4, 0],
  [18, 4, 0],
  [22, 2, 2],
  [22, 3, 2],
  [26, 4, 1]
]

/** Центры выравнивающих узоров по версиям 1..10 (у версии 1 их нет). */
const ALIGNMENT: number[][] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50]
]

// ───────────────────────────── арифметика GF(256) ─────────────────────────────

const GF_EXP = new Uint8Array(512)
const GF_LOG = new Uint8Array(256)

{
  let value = 1
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = value
    GF_LOG[value] = i
    value <<= 1
    // Примитивный многочлен QR: x^8 + x^4 + x^3 + x^2 + 1.
    if ((value & 0x100) !== 0) value ^= 0x11d
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255] as number
}

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0
  return GF_EXP[(GF_LOG[a] as number) + (GF_LOG[b] as number)] as number
}

/** Порождающий многочлен кода Рида — Соломона степени `degree`. */
function generatorPoly(degree: number): number[] {
  let poly = [1]
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(poly.length + 1).fill(0)
    for (let j = 0; j < poly.length; j++) {
      next[j] = (next[j] as number) ^ gfMul(poly[j] as number, 1)
      next[j + 1] = (next[j + 1] as number) ^ gfMul(poly[j] as number, GF_EXP[i] as number)
    }
    poly = next
  }
  return poly
}

/** Слова коррекции блока: остаток от деления на порождающий многочлен. */
function errorCorrection(data: number[], ecLength: number): number[] {
  const generator = generatorPoly(ecLength)
  const remainder = new Array<number>(ecLength).fill(0)
  for (const byte of data) {
    const factor = byte ^ (remainder[0] as number)
    remainder.shift()
    remainder.push(0)
    if (factor !== 0) {
      for (let i = 0; i < ecLength; i++) {
        remainder[i] = (remainder[i] as number) ^ gfMul(generator[i + 1] as number, factor)
      }
    }
  }
  return remainder
}

// ──────────────────────────────── поток данных ────────────────────────────────

/** Сколько байтов данных влезает в версию (за вычетом заголовка режима). */
function byteCapacity(version: number): number {
  const [ecPerBlock, group1, group2] = EC_BLOCKS_M[version - 1] as [number, number, number]
  const dataCodewords = (TOTAL_CODEWORDS[version - 1] as number) - ecPerBlock * (group1 + group2)
  // Заголовок: 4 бита режима + 8 бит длины (версии 1..9) либо 16 (версия 10+).
  const headerBits = version < 10 ? 12 : 20
  return dataCodewords - Math.ceil(headerBits / 8)
}

/**
 * Раскладка блоков версии. Экспорт нужен тесту-декодеру: чтобы собрать
 * кодовые слова обратно, надо знать ту же нарезку на блоки.
 */
export function blockLayout(version: number): {
  ecPerBlock: number
  blocks: number
  dataCodewords: number
} {
  const [ecPerBlock, group1, group2] = EC_BLOCKS_M[version - 1] as [number, number, number]
  const blocks = group1 + group2
  return {
    ecPerBlock,
    blocks,
    dataCodewords: (TOTAL_CODEWORDS[version - 1] as number) - ecPerBlock * blocks
  }
}

export function pickVersion(byteLength: number): number {
  for (let version = 1; version <= 10; version++) {
    if (byteCapacity(version) >= byteLength) return version
  }
  throw new QrTooLongError(byteLength)
}

/** Кодовые слова данных: режим, длина, полезная нагрузка, дополнение. */
function dataCodewordsOf(bytes: Uint8Array, version: number): number[] {
  const [ecPerBlock, group1, group2] = EC_BLOCKS_M[version - 1] as [number, number, number]
  const total = (TOTAL_CODEWORDS[version - 1] as number) - ecPerBlock * (group1 + group2)

  const bits: number[] = []
  const push = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >> i) & 1)
  }

  push(0b0100, 4) // байтовый режим
  push(bytes.length, version < 10 ? 8 : 16)
  for (const byte of bytes) push(byte, 8)

  // Терминатор — до четырёх нулей, дальше выравнивание до целого байта.
  const capacityBits = total * 8
  for (let i = 0; i < 4 && bits.length < capacityBits; i++) bits.push(0)
  while (bits.length % 8 !== 0) bits.push(0)

  const codewords: number[] = []
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0
    for (let j = 0; j < 8; j++) byte = (byte << 1) | (bits[i + j] as number)
    codewords.push(byte)
  }
  // Дополняющие байты стандарта — чередование 0xEC/0x11.
  const padding = [0xec, 0x11]
  while (codewords.length < total) codewords.push(padding[codewords.length % 2] as number)
  return codewords
}

/** Данные и коррекция, перемешанные по блокам так, как требует стандарт. */
function interleave(codewords: number[], version: number): number[] {
  const [ecPerBlock, group1, group2] = EC_BLOCKS_M[version - 1] as [number, number, number]
  const shortLength = Math.floor(codewords.length / (group1 + group2))

  const dataBlocks: number[][] = []
  const ecBlocks: number[][] = []
  let offset = 0
  for (let i = 0; i < group1 + group2; i++) {
    const length = i < group1 ? shortLength : shortLength + 1
    const block = codewords.slice(offset, offset + length)
    offset += length
    dataBlocks.push(block)
    ecBlocks.push(errorCorrection(block, ecPerBlock))
  }

  const result: number[] = []
  const longest = Math.max(...dataBlocks.map(block => block.length))
  for (let i = 0; i < longest; i++) {
    for (const block of dataBlocks) if (i < block.length) result.push(block[i] as number)
  }
  for (let i = 0; i < ecPerBlock; i++) {
    for (const block of ecBlocks) result.push(block[i] as number)
  }
  return result
}

// ──────────────────────────────── матрица ────────────────────────────────

type Grid = (boolean | null)[][]

/**
 * Карта служебных модулей версии: `true` — узор или поле формата, маска на них
 * не действует и данных там нет.
 *
 * Экспортируется ради теста: он раскодирует собственный QR обратно и сверит
 * строку. Без этой карты декодер пришлось бы писать «на глазок», и он бы
 * проверял сам себя.
 */
export function functionModules(version: number): boolean[][] {
  const size = 17 + version * 4
  const grid: Grid = Array.from({ length: size }, () => new Array<boolean | null>(size).fill(null))
  placeFunctionPatterns(grid, version)
  return grid.map(row => row.map(cell => cell !== null))
}

function placeFunctionPatterns(grid: Grid, version: number): void {
  const size = grid.length

  const finder = (row: number, col: number): void => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const y = row + r
        const x = col + c
        if (y < 0 || y >= size || x < 0 || x >= size) continue
        const inner = r >= 0 && r <= 6 && c >= 0 && c <= 6
        const ring = r === 0 || r === 6 || c === 0 || c === 6
        const core = r >= 2 && r <= 4 && c >= 2 && c <= 4
        // Всё, что вокруг узора, — обязательный светлый разделитель.
        grid[y]![x] = inner && (ring || core)
      }
    }
  }
  finder(0, 0)
  finder(0, size - 7)
  finder(size - 7, 0)

  // Синхродорожки: чередование по строке и столбцу с индексом 6.
  for (let i = 8; i < size - 8; i++) {
    grid[6]![i] = i % 2 === 0
    grid[i]![6] = i % 2 === 0
  }

  const centers = ALIGNMENT[version - 1] as number[]
  const last = centers.length - 1
  for (let i = 0; i <= last; i++) {
    for (let j = 0; j <= last; j++) {
      // Три угловые позиции заняты поисковыми узорами: выравнивающего там нет.
      // Проверяем именно ПОЗИЦИЮ, а не «занята ли клетка»: центр вроде (6, 22)
      // попадает на синхродорожку, и это законный выравнивающий узор.
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) continue
      const row = centers[i] as number
      const col = centers[j] as number
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          grid[row + r]![col + c] = Math.max(Math.abs(r), Math.abs(c)) !== 1
        }
      }
    }
  }

  // Тёмный модуль: он есть всегда и не несёт данных.
  grid[size - 8]![8] = true

  // Резервируем поля информации о формате — заполним после выбора маски.
  for (let i = 0; i < 9; i++) {
    if (grid[8]![i] === null) grid[8]![i] = false
    if (grid[i]![8] === null) grid[i]![8] = false
  }
  for (let i = 0; i < 8; i++) {
    if (grid[8]![size - 1 - i] === null) grid[8]![size - 1 - i] = false
    if (grid[size - 1 - i]![8] === null) grid[size - 1 - i]![8] = false
  }

  if (version >= 7) {
    for (let i = 0; i < 18; i++) {
      const row = Math.floor(i / 3)
      const col = size - 11 + (i % 3)
      grid[row]![col] = false
      grid[col]![row] = false
    }
  }
}

/**
 * Змейка справа налево по парам столбцов, пропуская столбец синхродорожки.
 *
 * Байты за концом потока читаются как нули — это и есть «добивочные биты»
 * стандарта (у версий 2..6 их семь), отдельного шага для них не нужно.
 */
function placeData(grid: Grid, codewords: number[]): void {
  const size = grid.length
  let bitIndex = 0
  let upward = true

  for (let right = size - 1; right >= 1; right -= 2) {
    // Столбец 6 целиком занят синхродорожкой: пара столбцов сдвигается влево.
    if (right === 6) right = 5
    for (let step = 0; step < size; step++) {
      const row = upward ? size - 1 - step : step
      for (const col of [right, right - 1]) {
        if (grid[row]![col] !== null) continue
        const byte = codewords[bitIndex >> 3]
        const bit = byte === undefined ? 0 : (byte >> (7 - (bitIndex & 7))) & 1
        grid[row]![col] = bit === 1
        bitIndex++
      }
    }
    upward = !upward
  }
}

const MASKS: ((row: number, col: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  r => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0
]

/**
 * Штраф по четырём правилам стандарта: длинные полосы одного цвета, блоки 2×2,
 * узор, похожий на поисковый, и перекос доли тёмного. Маска выбирается с
 * наименьшим штрафом — сканеру так заметно легче.
 */
function penalty(modules: boolean[][]): number {
  const size = modules.length
  let score = 0

  const line = (get: (i: number, j: number) => boolean): void => {
    for (let i = 0; i < size; i++) {
      let run = 1
      for (let j = 1; j < size; j++) {
        if (get(i, j) === get(i, j - 1)) {
          run++
          if (run === 5) score += 3
          else if (run > 5) score += 1
        } else run = 1
      }
    }
  }
  line((i, j) => modules[i]![j]!)
  line((i, j) => modules[j]![i]!)

  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const value = modules[r]![c]
      if (
        value === modules[r]![c + 1] &&
        value === modules[r + 1]![c] &&
        value === modules[r + 1]![c + 1]
      ) {
        score += 3
      }
    }
  }

  const FINDER_LIKE = [true, false, true, true, true, false, true]
  const matches = (cells: boolean[], at: number): boolean =>
    FINDER_LIKE.every((expected, i) => cells[at + i] === expected)
  const quiet = (cells: boolean[], from: number, to: number): boolean => {
    for (let i = from; i < to; i++) if (cells[i] !== false) return false
    return true
  }
  const scanLine = (cells: boolean[]): void => {
    for (let i = 0; i + 7 <= cells.length; i++) {
      if (!matches(cells, i)) continue
      if (i >= 4 && quiet(cells, i - 4, i)) score += 40
      if (i + 11 <= cells.length && quiet(cells, i + 7, i + 11)) score += 40
    }
  }
  for (let i = 0; i < size; i++) {
    scanLine(modules[i] as boolean[])
    scanLine(modules.map(row => row[i] as boolean))
  }

  const dark = modules.flat().filter(Boolean).length
  const percent = (dark * 100) / (size * size)
  score += Math.floor(Math.abs(percent - 50) / 5) * 10
  return score
}

/** Информация о формате: уровень M (0b00) + маска, BCH(15,5), маскируется 0x5412. */
function formatBits(mask: number): number {
  const data = (0b00 << 3) | mask
  let bch = data << 10
  for (let i = 14; i >= 10; i--) {
    if ((bch >> i) % 2 !== 0) bch ^= 0b10100110111 << (i - 10)
  }
  return ((data << 10) | bch) ^ 0b101010000010010
}

/** Информация о версии (только 7+): BCH(18,6). */
function versionBits(version: number): number {
  let bch = version << 12
  for (let i = 17; i >= 12; i--) {
    if ((bch >> i) % 2 !== 0) bch ^= 0b1111100100 << (i - 12)
  }
  return (version << 12) | bch
}

function writeFormatAndVersion(modules: boolean[][], version: number, mask: number): void {
  const size = modules.length
  const bits = formatBits(mask)
  const bit = (index: number): boolean => ((bits >> index) & 1) === 1

  // Первая копия — вокруг верхнего левого поискового узора.
  for (let i = 0; i <= 5; i++) modules[i]![8] = bit(i)
  modules[7]![8] = bit(6)
  modules[8]![8] = bit(7)
  modules[8]![7] = bit(8)
  for (let i = 9; i <= 14; i++) modules[8]![14 - i] = bit(i)

  // Вторая копия — разорвана между правым верхним и левым нижним углами.
  for (let i = 0; i <= 7; i++) modules[8]![size - 1 - i] = bit(i)
  for (let i = 8; i <= 14; i++) modules[size - 15 + i]![8] = bit(i)
  modules[size - 8]![8] = true

  if (version < 7) return
  const vBits = versionBits(version)
  for (let i = 0; i < 18; i++) {
    const value = ((vBits >> i) & 1) === 1
    const row = Math.floor(i / 3)
    const col = size - 11 + (i % 3)
    modules[row]![col] = value
    modules[col]![row] = value
  }
}

/**
 * Строит матрицу QR для строки. Кидает `QrTooLongError`, если данные не влезают
 * в версию 10 — вызывающий код обязан это обработать и показать текст вместо
 * картинки (см. `TotpPanel`).
 */
export function encodeQr(text: string): QrMatrix {
  const bytes = new TextEncoder().encode(text)
  const version = pickVersion(bytes.length)
  const size = 17 + version * 4

  const codewords = interleave(dataCodewordsOf(bytes, version), version)

  const base: Grid = Array.from({ length: size }, () => new Array<boolean | null>(size).fill(null))
  placeFunctionPatterns(base, version)
  // Запоминаем, какие модули служебные: маска на них не действует.
  const isFunction = base.map(row => row.map(cell => cell !== null))
  placeData(base, codewords)

  let best: boolean[][] | null = null
  let bestScore = Number.POSITIVE_INFINITY
  for (let mask = 0; mask < 8; mask++) {
    const candidate = base.map((row, r) =>
      row.map((cell, c) => {
        const value = cell === true
        if (isFunction[r]![c] === true) return value
        return (MASKS[mask] as (row: number, col: number) => boolean)(r, c) ? !value : value
      })
    )
    writeFormatAndVersion(candidate, version, mask)
    const score = penalty(candidate)
    if (score < bestScore) {
      bestScore = score
      best = candidate
    }
  }

  return { size, modules: best as boolean[][] }
}
